import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import { PANEL_DEFAULT_WIDTH } from '../lib/panelWidth';
import { fetchMessages } from '../lib/sessionsApi';
import { streamChatResponse } from '../lib/streamChat';
import type {
  ChatErrorCode,
  ChatStreamEvent,
  Message,
} from '../types/types.ts';

export type ChatMessage = {
  id: string;
  role: 'USER' | 'AGENT';
  text: string;
};

type SendOptions = {
  getAccessToken: () => Promise<string | undefined>;
  standortId: string;
};

type LoadOptions = Pick<SendOptions, 'getAccessToken'>;

type ChatData = {
  sessionId: string | undefined;
  messages: ChatMessage[];
  answer: string;
  isStreaming: boolean;
  error: ChatErrorCode | undefined;
  abortController: AbortController | undefined;
};

type ChatState = ChatData & {
  userId: string | undefined;
  setUser: (userId: string) => void;
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  panelWidth: number;
  setPanelWidth: (width: number) => void;
  isHistoryOpen: boolean;
  openHistory: () => void;
  closeHistory: () => void;
  loadSession: (
    sessionId: string,
    options: LoadOptions,
  ) => Promise<ChatErrorCode | undefined>;
  sendQuestion: (question: string, options: SendOptions) => Promise<void>;
  cancel: () => void;
  reset: () => void;
};

const initialChat: ChatData = {
  sessionId: undefined,
  messages: [],
  answer: '',
  isStreaming: false,
  error: undefined,
  abortController: undefined,
};

const toChatMessage = ({ id, role, text }: Message): ChatMessage => ({
  id,
  role,
  text,
});

const createMessage = (
  role: ChatMessage['role'],
  text: string,
): ChatMessage => ({ id: crypto.randomUUID(), role, text });

function finishAnswer(state: ChatState): Partial<ChatState> {
  const messages = state.answer
    ? [...state.messages, createMessage('AGENT', state.answer)]
    : state.messages;
  return {
    messages,
    answer: '',
    isStreaming: false,
    abortController: undefined,
  };
}

// Verarbeitet ein einzelnes Stream-Event und liefert den daraus resultierenden State
function applyEvent(
  state: ChatState,
  event: ChatStreamEvent,
): Partial<ChatState> {
  switch (event.type) {
    case 'session':
      return { sessionId: event.sessionId };
    case 'chunk':
      return { answer: state.answer + event.chunk };
    case 'error':
      return {
        ...finishAnswer(state),
        error: event.error.code,
        // Unbekannte oder fremde Session: die nächste Frage startet einen neuen Chat
        ...(event.error.code === 'SESSION_NOT_FOUND' && {
          sessionId: undefined,
        }),
      };
    case 'done':
      return finishAnswer(state);
  }
}

export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      ...initialChat,
      userId: undefined as string | undefined,
      // Gehört der gespeicherte Chat einem anderen User, wird er verworfen
      setUser: (userId) => {
        if (get().userId === userId) {
          return;
        }
        get().abortController?.abort();
        set({ ...initialChat, isOpen: false, isHistoryOpen: false, userId });
      },
      isOpen: false,
      open: () => set({ isOpen: true }),
      close: () => set({ isOpen: false, isHistoryOpen: false }),
      toggle: () => set((state) => ({ isOpen: !state.isOpen })),
      panelWidth: PANEL_DEFAULT_WIDTH,
      setPanelWidth: (panelWidth) => set({ panelWidth }),
      isHistoryOpen: false,
      openHistory: () => set({ isHistoryOpen: true }),
      closeHistory: () => set({ isHistoryOpen: false }),
      loadSession: async (sessionId, { getAccessToken }) => {
        const result = await fetchMessages(sessionId, { getAccessToken });
        if ('error' in result) {
          return result.error;
        }
        // Erst nach erfolgreichem Laden einen laufenden Stream abbrechen
        get().abortController?.abort();
        set({
          ...initialChat,
          sessionId,
          messages: result.data.map(toChatMessage),
          isHistoryOpen: false,
        });
        return undefined;
      },
      sendQuestion: async (question, { getAccessToken, standortId }) => {
        if (get().isStreaming) {
          return;
        }
        const abortController = new AbortController();
        set((state) => ({
          messages: [...state.messages, createMessage('USER', question)],
          answer: '',
          isStreaming: true,
          error: undefined,
          abortController,
        }));
        await streamChatResponse({
          request: { question, sessionId: get().sessionId, standortId },
          getAccessToken,
          signal: abortController.signal,
          onEvent: (event) => {
            // Events eines abgebrochenen oder zurückgesetzten Streams ignorieren
            if (get().abortController === abortController) {
              set((state) => applyEvent(state, event));
            }
          },
        });
      },
      // Bricht den laufenden Stream ab und übernimmt die bisherige Teilantwort als Nachricht
      cancel: () => {
        get().abortController?.abort();
        set(finishAnswer(get()));
      },
      // Bricht einen laufenden Stream ab und setzt den gesamten Chat-Zustand zurück
      reset: () => {
        get().abortController?.abort();
        set(initialChat);
      },
    }),
    {
      name: 'e2n-ai-chat',
      storage: createJSONStorage(() => sessionStorage),
      // Speichert den Zustand in die Browser-Session.
      partialize: (state) => ({
        userId: state.userId,
        isOpen: state.isOpen,
        panelWidth: state.panelWidth,
        sessionId: state.sessionId,
        messages: state.messages,
      }),
    },
  ),
);
